import React from "react"
import { graphql } from "gatsby"
import { withPrismicPreview } from "gatsby-plugin-prismic-previews"

import ActionCall, { ActionCallProps } from "legacy/components/shared/ActionCall"
import CenteredWysiwyg from "legacy/components/CenteredWysiwyg"
import { Carousel as ChallengeCarousel } from "legacy/components/Redesign/Carousel/Carousel"
import { Column } from "legacy/components/Redesign/Column/Column"
import CurvedTextSection from "legacy/components/CurvedTextSection"
import Disclaimer from "legacy/components/Disclaimer"
import { FiftyFiftySplit } from "legacy/components/Redesign/FiftyFiftySplit/FiftyFiftySplit"
import Hero from "legacy/components/Hero"
import { Hero as RHero } from "legacy/components/Redesign/Hero/Hero"
import HorizontalRule from "legacy/components/HorizontalRule"
import ImageAndTextSection from "legacy/components/ImageAndTextSection"
import InteractiveList from "legacy/components/InteractiveList"
import JumpNavigation from "legacy/components/JumpNavigation"
import JobBoard from "legacy/components/JobBoard"
import Layout from "./Layout"
import { Maybe } from "types/Maybe"
import {
  CenteredWysiwygType,
  ChallengeCarouselType,
  ColumnType,
  CurvedTextType,
  DisclaimerType,
  FiftyFiftySplitType,
  HeroType,
  HorizontalRuleType,
  ImageAndTextType,
  InteractiveListType,
  JumpNavigationType,
  PullQuoteType,
  OneColumnType,
  RHeroType,
  SectionTitleType,
  SideBySideType,
  SplitHeroType,
  ThreeColumnSectionType,
  WideHeroType,
} from "types/shared-components"
import {
  PrismicPageDataBodyJobBoard,
  PrismicPageDataBodyRecentPosts,
  Query,
} from "types/__generated__/gatsby-graphql"
import PullQuote from "legacy/components/PullQuote"
import OneColumnSection from "legacy/components/OneColumnSection"
import RecentPosts from "legacy/components/page-components/BlogHome/RecentPosts"
import { SectionTitle } from "legacy/components/Redesign/SectionTitle/SectionTitle"
import SEO from "legacy/components/shared/SEO"
import { SideBySide } from "legacy/components/Redesign/SideBySide/SideBySide"
import SplitHero from "legacy/components/SplitHero"
import ThreeColumnSection from "legacy/components/ThreeColumnSection"
import WideHero from "legacy/components/WideHero"
import { defaultCanonicalLink, linkResolver } from "src/legacy/utils"

interface OwnProps {
  data: Query
}

type PageProps = OwnProps

export const pageQuery = graphql`
  query PageQuery($uid: String!, $language: String! = "en") {
    locales: allLocale(
      filter: { ns: { in: ["shared"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    prismicPage(uid: { eq: $uid }) {
      _previewable
      id
      data {
        body {
          ... on PrismicPageDataBodyCallToAction {
            id
            primary {
              cta_background_color
              cta_button_text
              cta_eyebrow
              cta_input_placeholder
              cta_intro
              form_confirmation_text
              form_error_text
              form_name
              image {
                gatsbyImageData
                alt
                url
              }
              input_type
              title1 {
                richText
                text
                html
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyColumn {
            id
            primary {
              align_columns
              button_style
              button_text
              button_url {
                link_type
                url
              }
              column_slice_title {
                text
              }
              title_alignment
            }
            items {
              button_text
              button_url {
                link_type
                url
              }
              eyebrow
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              image_size
              column_intro {
                richText
                html
                text
              }
              column_title {
                text
              }
            }
          }
          ... on PrismicPageDataBodyCurvedHero {
            id
            items {
              button_text
              button_url {
                document {
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                }
                link_type
                type
                uid
                url
              }
              headline {
                html
                richText
                text
              }
            }
            primary {
              app_store_buttons
              background_image {
                url
              }
              intro
            }
            slice_type
          }
          ... on PrismicPageDataBodyCurvedText {
            id
            items {
              body_text
            }
            primary {
              background_color
              button_color
              button_text
              button_url {
                document {
                  ... on PrismicBlogPost {
                    id
                    type
                    uid
                    url
                  }
                  ... on PrismicLegalPage {
                    id
                    type
                    uid
                    url
                  }
                  ... on PrismicHomepage {
                    id
                    tags
                    type
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    type
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    type
                    uid
                    url
                  }
                }
                id
                uid
                url
              }
              curved_text
            }
            slice_type
          }
          ... on PrismicPageDataBodyDisclaimer {
            id
            primary {
              background_color
              disclaimer_text {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyHero {
            id
            items {
              button_color
              button_text
              button_type
              button_url {
                document {
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                }
                link_type
                type
                uid
                url
              }
            }
            primary {
              app_store_buttons
              background_color
              font_color
              eyebrow
              headline {
                html
                richText
                text
              }
              headline_font_size
              hero_width
              hero_width_mobile
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              image_should_be_background
              intro
              mobile_image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyHeroR {
            id
            primary {
              background
              button_text
              button_url {
                link_type
                type
                uid
                url
              }
              eyebrow
              form_name
              form_confirmation_text
              form_error_text
              form_submit_button_text
              hero_title {
                text
              }
              inline_image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              inline_image_size
              input_placeholder
              input_type
              has_form
              hero_intro {
                richText
                html
                text
              }
              show_only_during_split_test
              split_test_branch_name
              title_image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              title_image_size
              title_size
            }
            slice_type
          }
          ... on PrismicPageDataBodyHorizontalRule {
            id
            primary {
              color
              rule_width
              vertical_spacing
            }
            slice_type
          }
          ... on PrismicPageDataBodyImageAndText {
            id
            items {
              body_text_wysiwyg {
                html
                richText
                text
              }
              button_color
              button_text
              button_type
              button_url {
                document {
                  ... on PrismicBlogPost {
                    id
                    type
                    uid
                    url
                  }
                  ... on PrismicLegalPage {
                    id
                    type
                    uid
                    url
                  }
                  ... on PrismicHomepage {
                    id
                    tags
                    type
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    type
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    type
                    uid
                    url
                  }
                }
                id
                link_type
                uid
                url
              }
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              removed_bottom_padding
              title {
                html
                richText
                text
              }
            }
            primary {
              background_color
              full_bleed_images
              headline {
                html
                richText
                text
              }
              image_starts_on
            }
            slice_type
          }
          ... on PrismicPageDataBodyJumpNavigation {
            id
            primary {
              jump_navigation_title {
                richText
                text
                html
              }
              section_header_level
            }
            slice_type
          }
          ... on PrismicPageDataBodyInteractiveList {
            id
            items {
              wysiwyg {
                html
                richText
                text
              }
              button_text
              button_url {
                document {
                  ... on PrismicBlogPost {
                    id
                    uid
                    url
                  }
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicHomepage {
                    id
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicNavigation {
                    id
                    url
                  }
                }
                id
                link_type
                uid
                url
              }
              expandable_list_item
              list_item_name {
                html
                richText
                text
              }
            }
            primary {
              background_image {
                alt
                url
              }
              background_image_position
              mobile_background_image {
                alt
                url
              }
              mobile_background_image_position
              one_column_desktop
              reduced_top_padding
              wysiwyg {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyJobBoard {
            id
            primary {
              headline {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyOneColumn {
            id
            items {
              button_color
              button_text
              button_type
              button_url {
                document {
                  ... on PrismicBlogPost {
                    id
                    uid
                    url
                  }
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicHomepage {
                    id
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                }
                type
                uid
                url
              }
            }
            primary {
              background_color
              center_column_content
              eyebrow
              full_bleed_section
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              mobile_image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              section_width
              vertical_padding
              wysiwyg {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyPullQuote {
            id
            primary {
              author
              quote_text
            }
            slice_type
          }
          ... on PrismicPageDataBodyRecentPosts {
            id
            primary {
              headline {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodySideBySide {
            id
            primary {
              mobile_alignment
              side_by_side_title {
                text
              }
            }
            items {
              button_style
              button_text
              button_url {
                link_type
                url
              }
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              image_size
              side_by_side_intro {
                html
                richText
                text
              }
              side_title
            }
            slice_type
          }
          ... on PrismicPageDataBodySplitHero {
            id
            slice_type
            items {
              button_text
              button_url {
                url
                document {
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                }
                id
                link_type
                uid
                url
              }
            }
            primary {
              app_store_buttons
              background_color
              headline {
                html
                richText
                text
              }
              intro
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
            }
          }
          ... on PrismicPageDataBodyThreeColumn {
            id
            items {
              background_color
              button_text
              button_url {
                document {
                  ... on PrismicBlogPost {
                    id
                    uid
                    url
                  }
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicHomepage {
                    id
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicNavigation {
                    id
                    url
                  }
                }
                id
                link_type
                uid
                url
              }
              eyebrow
              footnote
              headline {
                html
                richText
                text
              }
              headline_font_size
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              small_image
              wysiwyg {
                html
                richText
                text
              }
            }
            primary {
              background_color
              button_text
              button_type
              button_url {
                link_type
                type
                uid
                url
              }
              button_background_color
              button_text_color
              center_column_content
              headline {
                html
                richText
                text
              }
              headline_font_size
              headline_text_alignment
              headline_text_alignment_mobile
              paginate_items
              removed_vertical_padding
              wysiwyg {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyWideHero {
            id
            primary {
              background_color
              eyebrow
              headline {
                html
                richText
                text
              }
              hero_image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyCenteredWysiwyg {
            id
            primary {
              button_text
              button_url {
                document {
                  ... on PrismicBlogPost {
                    id
                    uid
                    url
                  }
                  ... on PrismicLegalPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicHomepage {
                    id
                    url
                  }
                  ... on PrismicOurMissionPage {
                    id
                    uid
                    url
                  }
                  ... on PrismicPage {
                    id
                    uid
                    url
                  }
                }
                type
                uid
                url
              }
              wysiwyg {
                html
                richText
                text
              }
            }
            slice_type
          }
          ... on PrismicPageDataBodyChallengeCarousel {
            id
            items {
              challenge_icon {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              challenge_name
              challenge_value
              show_on_slide
            }
            primary {
              carousel_intro {
                richText
                html
                text
              }
              carousel_title {
                text
                richText
                html
              }
              slide_title_1
              slide_title_10
              slide_title_11
              slide_title_12
              slide_title_2
              slide_title_3
              slide_title_4
              slide_title_5
              slide_title_6
              slide_title_7
              slide_title_8
              slide_title_9
              small_challenge_images
              title_size
            }
            slice_type
          }
          ... on PrismicPageDataBodyFiftyFiftySplit {
            id
            primary {
              background
              button_style
              button_text
              button_type
              button_url {
                link_type
                type
                uid
                url
              }
              center_image
              eyebrow
              fifty_fifty_split_title {
                text
              }
              full_sized_image
              image {
                alt
                dimensions {
                  height
                  width
                }
                url
              }
              image_placement
              image_mobile_placement
              fifty_fifty_intro {
                richText
                html
                text
              }
              mobile_text_alignment
              title_size
            }
            slice_type
          }
          ... on PrismicPageDataBodySectionTitle {
            id
            primary {
              section_title {
                text
              }
              title_alignment
              title_size
              title_width
            }
            slice_type
          }
        }
        canonical_url
        og_description
        og_image {
          url
        }
        og_title
        title {
          text
        }
      }
      uid
      url
    }
    site {
      siteMetadata {
        siteURL
      }
    }
  }
`

export const Head = pageData => {
  const prismicPage = pageData?.data?.prismicPage
  const { canonical_url, og_description, og_image, og_title, title } =
    prismicPage?.data
  const canonicalUrl =
    canonical_url ||
    defaultCanonicalLink(
      prismicPage?.url,
      pageData?.data?.site?.siteMetaData?.siteUrl
    )

  return (
    <SEO
      canonicalUrl={canonicalUrl}
      ogDescription={og_description}
      ogImage={og_image?.url}
      ogTitle={og_title}
      ogType="page"
      title={title?.text}
    />
  )
}

const Page: React.FC<PageProps> = ({
  data: { prismicPage },
}): React.ReactElement | null => {
  if (!prismicPage?.data) return null

  const pageData = prismicPage.data
  const componentsData = pageData.body

  return (
    <Layout>
      {componentsData?.map((component, index) => {
        const componentName = component?.slice_type
        const key = `${componentName}_${index}`

        switch (componentName) {
          case "call_to_action":
            return (
              <ActionCall
                data={component as Maybe<ActionCallProps>}
                key={key}
              />
            )
          case "centered_wysiwyg":
            return (
              <CenteredWysiwyg
                data={component as Maybe<CenteredWysiwygType>}
                key={key}
              />
            )
          case "challenge_carousel":
            return (
              <ChallengeCarousel
                data={component as Maybe<ChallengeCarouselType>}
                key={key}
              />
            )
          case "column":
            return <Column data={component as Maybe<ColumnType>} key={key} />
          case "curved_text":
            return (
              <CurvedTextSection
                data={component as Maybe<CurvedTextType>}
                key={key}
              />
            )
          case "disclaimer":
            return (
              <Disclaimer data={component as Maybe<DisclaimerType>} key={key} />
            )
          case "fifty_fifty_split":
            return (
              <FiftyFiftySplit
                data={component as Maybe<FiftyFiftySplitType>}
                key={key}
              />
            )
          case "hero":
            return <Hero data={component as Maybe<HeroType>} key={key} />
          case "hero1":
            return <RHero data={component as Maybe<RHeroType>} key={key} />
          case "horizontal_rule":
            return (
              <HorizontalRule
                data={component as Maybe<HorizontalRuleType>}
                key={key}
              />
            )
          case "image_and_text":
            return (
              <ImageAndTextSection
                data={component as Maybe<ImageAndTextType>}
                key={key}
              />
            )
          case "interactive_list":
            return (
              <InteractiveList
                data={component as Maybe<InteractiveListType>}
                key={key}
              />
            )
          case "jump_navigation":
            return (
              <JumpNavigation
                data={component as Maybe<JumpNavigationType>}
                key={key}
              />
            )
          case "one_column":
            return (
              <OneColumnSection
                data={component as Maybe<OneColumnType>}
                key={key}
              />
            )
          case "pull_quote":
            return (
              <PullQuote data={component as Maybe<PullQuoteType>} key={key} />
            )
          case "recent_posts":
            return (
              <RecentPosts
                data={component as Maybe<PrismicPageDataBodyRecentPosts>}
                key={key}
              />
            )
          case "section_title":
            return (
              <SectionTitle
                data={component as Maybe<SectionTitleType>}
                key={key}
              />
            )
          case "side_by_side":
            return (
              <SideBySide data={component as Maybe<SideBySideType>} key={key} />
            )
          case "split_hero":
            return (
              <SplitHero data={component as Maybe<SplitHeroType>} key={key} />
            )
          case "three-column":
            return (
              <ThreeColumnSection
                data={component as Maybe<ThreeColumnSectionType>}
                key={key}
              />
            )
          case "wide_hero":
            return (
              <WideHero data={component as Maybe<WideHeroType>} key={key} />
            )
          default:
            return null
        }
      })}
    </Layout>
  )
}

export default withPrismicPreview(Page, [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
    linkResolver,
  },
])
